import { MenuItem, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface Props<T extends string | number | boolean> {
  id: string;
  label: string;
  value: unknown;
  options: readonly T[];
  disabled?: boolean;
  required?: boolean;
  /** Validation message; replaces the hint while set. */
  error?: string;
  fallbackHint?: string;
  /** Explains a configured value outside the presets; defaults to the JSON-managed hint. */
  customHint?: string;
  /** Label of the empty choice; defaults to "Default". */
  unsetLabel?: string;
  /** Display text of an option; defaults to the value itself. */
  optionLabel?: (option: T) => string;
  onChange: (value: T | undefined) => void;
}

export const VoicePresetSelect = <T extends string | number | boolean>({
  id,
  label,
  value,
  options,
  disabled,
  required,
  error,
  fallbackHint,
  customHint,
  unsetLabel,
  optionLabel,
  onChange,
}: Props<T>) => {
  const { t } = useTranslation();
  const index = options.findIndex((option) => option === value);
  const configured = value !== undefined && index < 0;
  const jsonManaged = customHint ?? t('superAdminManageVoice.advanced.jsonManaged');
  const scalar = ['string', 'number', 'boolean'].includes(typeof value);
  const configuredLabel = scalar ? `${String(value)} — ${jsonManaged}` : jsonManaged;
  const display = (option: T) =>
    optionLabel
      ? optionLabel(option)
      : typeof option === 'boolean'
        ? t(`superAdminManageVoice.advanced.${option}`)
        : String(option);

  return (
    <TextField
      id={id}
      select
      fullWidth
      margin="dense"
      label={label}
      required={required}
      disabled={disabled}
      error={Boolean(error)}
      value={index >= 0 ? String(index) : configured ? 'json' : ''}
      helperText={
        error ??
        (configured ? jsonManaged : (fallbackHint ?? t('superAdminManageVoice.advanced.optional')))
      }
      onChange={(event) => {
        const selected = event.target.value;
        if (selected === '') onChange(undefined);
        else {
          const option = options[Number(selected)];
          if (option !== undefined) onChange(option);
        }
      }}>
      {!required && (
        <MenuItem value="">{unsetLabel ?? t('superAdminManageVoice.advanced.unset')}</MenuItem>
      )}
      {configured && (
        <MenuItem value="json" disabled>
          {configuredLabel}
        </MenuItem>
      )}
      {options.map((option, optionIndex) => (
        <MenuItem key={String(option)} value={String(optionIndex)}>
          {display(option)}
        </MenuItem>
      ))}
    </TextField>
  );
};
